import { Component, OnInit, OnDestroy, ViewChild, ElementRef, Optional, Self, Input } from "@angular/core";
import { ControlValueAccessor, FormControl, NgControl } from "@angular/forms";
import { MatFormFieldControl } from "@angular/material/form-field";
import { Subject, Observable, startWith, map, takeUntil, BehaviorSubject, switchMap, combineLatest } from "rxjs";
import moment from "moment-timezone";
import { FocusMonitor } from "@angular/cdk/a11y";
import { coerceBooleanProperty } from "@angular/cdk/coercion";

interface TimezoneOption {
  name: string;
  offset: number;
  offsetFormatted: string;
}

@Component({
  selector: "app-timezone-select",
  templateUrl: "./timezone-select.component.html",
  styleUrls: ["./timezone-select.component.scss"],
  providers: [{ provide: MatFormFieldControl, useExisting: TimezoneSelectComponent }],
  host: {
    "[id]": "id",
    "[attr.aria-describedby]": "describedBy",
  },
})
export class TimezoneSelectComponent implements ControlValueAccessor, MatFormFieldControl<string>, OnInit, OnDestroy {
  static nextId = 0;
  readonly controlType = "app-timezone-select";
  id = `app-timezone-select-${TimezoneSelectComponent.nextId++}`;
  describedBy = "";
  stateChanges = new Subject<void>();

  timezoneSearchCtrl = new FormControl("");
  selectedTz = new FormControl<string>("");

  timezones$: Observable<TimezoneOption[]>;
  private allTimezones: TimezoneOption[] = [];

  focused = false;
  private _placeholder: string = "";
  private _required = false;
  private _disabled = false;

  @Input("disableControl") set disableControl(value: boolean) {
    this._disabled = value;
    this.stateChanges.next();
  }

  _timezonePreferences = new BehaviorSubject<string[]>([]);
  @Input("timezonePreferences") set timezonePreferences(value: string[]) {
    this._timezonePreferences.next(value);
  }

  // @Input("timezonePreferences") timezonePreferences: string[] = [];

  private _value: string | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private fm: FocusMonitor,
    @Optional() @Self() public ngControl: NgControl,
  ) {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }

    this.fm.monitor(this as any, true).subscribe((origin) => {
      this.focused = !!origin;
      this.stateChanges.next();
    });
  }

  // MatFormFieldControl API
  get value(): string | null {
    return this._value;
  }
  set value(val: string | null) {
    this._value = val;
    this.stateChanges.next();
  }

  get placeholder() {
    return this._placeholder;
  }
  set placeholder(plh: string) {
    this._placeholder = plh;
    this.stateChanges.next();
  }

  get required() {
    return this._required;
  }
  set required(req: boolean) {
    this._required = coerceBooleanProperty(req);
    this.stateChanges.next();
  }

  get disabled() {
    return this._disabled;
  }
  set disabled(dis: boolean) {
    this._disabled = coerceBooleanProperty(dis);
    this.stateChanges.next();
  }

  get empty() {
    return !this.selectedTz.value;
  }

  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  get errorState(): boolean {
    return !!(this.ngControl && this.ngControl.invalid && (this.ngControl.touched || this.ngControl.dirty));
  }

  private _onTouched: () => void = () => {};

  private _onChange: () => void = () => {};

  setDescribedByIds(ids: string[]) {
    this.describedBy = ids.join(" ");
  }

  onContainerClick() {
    // optional: you can programmatically open the select if desired
  }

  // ControlValueAccessor API
  writeValue(value: string | null): void {
    this._value = value;
    this.selectedTz.setValue(value, { emitEvent: false });
    this.stateChanges.next();
  }
  registerOnChange(fn: any): void {
    this.selectedTz.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(fn);
    this._onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.selectedTz.disable();
    } else {
      this.selectedTz.enable();
    }
  }

  ngOnInit() {
    this.allTimezones = moment.tz.names().map((name) => ({
      name,
      offset: moment.tz(name).utcOffset(),
      offsetFormatted: `UTC${moment.tz(name).format("Z")}`,
    }));

    const usedTimezones$ = this._timezonePreferences.asObservable().pipe(
      map((prefs) => {
        return prefs.length > 0 ? this.allTimezones.filter((tz) => prefs.includes(tz.name)) : this.allTimezones;
      }),
    );

    this.timezones$ = combineLatest([usedTimezones$, this.timezoneSearchCtrl.valueChanges.pipe(startWith(""))]).pipe(
      map(([timezones, search]) => {
        const searchLower = search.toLowerCase();
        return timezones.filter(
          (tz) => tz.name.toLowerCase().includes(searchLower) || tz.offsetFormatted.includes(search),
        );
      }),
    );

    if (this.ngControl?.control) {
      this.ngControl.control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
        if (value !== this.selectedTz.value) {
          this.selectedTz.setValue(value, { emitEvent: false });
          this._value = value;
          this.stateChanges.next();
        }
      });
    }
  }

  ngOnDestroy() {
    this.stateChanges.complete();
    this.destroy$.next();
    this.destroy$.complete();
    this.fm.stopMonitoring(this as any);
  }

  //  Extra
  @ViewChild("tzSearch") tzSearch!: ElementRef<HTMLInputElement>;

  onOpenedChange(opened: boolean) {
    if (opened) {
      setTimeout(() => this.tzSearch?.nativeElement.focus(), 0);
    } else {
      this._onTouched();
      this.stateChanges.next();
    }
  }
}
